import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Grid,
  TextField,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  Schedule,
  Sync,
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { DataGrid } from '@mui/x-data-grid';
import { importService } from '../../../services/importService';

interface ImportAdicionalModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedData {
  usuario_pppoe: string; // ID único (não se repete)
  valid: boolean;
  validationErrors?: string[];
  importStatus?: 'pending' | 'importing' | 'success' | 'error';
  importError?: string;
  [key: string]: any;
}

interface ImportStats {
  total: number;
  valid: number;
  invalid: number;
}

interface ImportProgress {
  nome: string;
  status: 'processando' | 'sucesso' | 'erro';
  erro?: string;
}

const steps = [
  'Configuração da API',
  'Análise e Importação'
];

export const ImportAdicionalModal: React.FC<ImportAdicionalModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState('https://www.seusite.com.br/admin/addons/sysup/apiv2/listarAdicionaisAtivos');
  const [apiToken, setApiToken] = useState('9f4c8db4c60a93f1a3a77a9dd11b2f5c49c634b9dcf32776f79c4fc343a3b07d');
  const [parsedData, setParsedData] = useState<ParsedData[]>([]);
  const [importStats, setImportStats] = useState<ImportStats>({
    total: 0,
    valid: 0,
    invalid: 0
  });
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ [key: string]: ImportProgress }>({});
  const [importCompleted, setImportCompleted] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Funções de seleção de checkbox
  const handleSelectRow = (usuario_pppoe: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(usuario_pppoe)) {
      newSelected.delete(usuario_pppoe);
    } else {
      newSelected.add(usuario_pppoe);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedRows.size === parsedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(parsedData.map((item) => item.usuario_pppoe)));
    }
  };

  const getFieldValidationErrors = (row: ParsedData): { [key: string]: boolean } => {
    const fieldErrors: { [key: string]: boolean } = {};

    // Verificar quais campos específicos têm erros
    if (!row.contrato_titulo) fieldErrors.contrato_titulo = true;
    if (!row.login) fieldErrors.login = true;
    if (!row.plano) fieldErrors.plano = true;
    if (!row.usuario_pppoe) fieldErrors.usuario_pppoe = true;
    if (!row.senha_pppoe) fieldErrors.senha_pppoe = true;
    if (!row.endereco_inst) fieldErrors.endereco_inst = true;
    if (!row.cidade_inst) fieldErrors.cidade_inst = true;
    if (!row.estado_inst) fieldErrors.estado_inst = true;
    if (!row.cep_inst) fieldErrors.cep_inst = true;

    return fieldErrors;
  };

  const cleanPhoneNumber = (phone: string): string => {
    if (!phone || phone.trim() === '') return '';

    // Remover todos os caracteres que não são números
    return phone.replace(/\D/g, '');
  };

  const parseApiData = (adicionaisData: any[]): ParsedData[] => {
    try {
      const data: ParsedData[] = [];

      adicionaisData.forEach((adicional) => {
        const adicionalData: ParsedData = {
          //DADOS DO CONTATO
          telefone: cleanPhoneNumber(adicional.telefone || ''),
          //DADOS DO CONTRATO DO CLIENTE
          contrato_titulo: adicional.contrato_titulo,
          login: adicional.login,
          usuario_pppoe: adicional.usuario_pppoe, // Login PPPoE (ID único - não se repete)
          senha_pppoe: adicional.senha_pppoe, // Senha PPPoE
          endereco_mac: adicional.endereco_mac || null,
          endereco_ip: adicional.endereco_ip || null,
          plano: adicional.plano || null,
          simultaneo: adicional.simultaneo, // Boolean para string
          //ENDEREÇO DO CONTRATO
          endereco_inst: adicional.endereco_inst,
          numero_inst: adicional.numero_inst,
          bairro_inst: adicional.bairro_inst,
          cidade_inst: adicional.cidade_inst,
          estado_inst: adicional.estado_inst,
          cep_inst: adicional.cep_inst,
          complemento_inst: adicional.complemento_inst,
          coordenadas: adicional.coordenadas,
          valid: true,
          validationErrors: []
        };

        // Validações básicas
        let isValid = true;
        let validationErrors: string[] = [];

        if (!adicionalData.contrato_titulo) {
          isValid = false;
          validationErrors.push('Título do contrato obrigatório');
        }

        if (!adicionalData.login) {
          isValid = false;
          validationErrors.push('Login obrigatório');
        }

        if (!adicionalData.plano) {
          isValid = false;
          validationErrors.push('Plano obrigatório');
        }

        if (!adicionalData.usuario_pppoe) {
          isValid = false;
          validationErrors.push('Usuário PPPoE obrigatório');
        }

        if (!adicionalData.senha_pppoe) {
          isValid = false;
          validationErrors.push('Senha PPPoE obrigatória');
        }

        if (!adicionalData.endereco_inst) {
          isValid = false;
          validationErrors.push('Endereço de instalação obrigatório');
        }

        if (!adicionalData.cidade_inst) {
          isValid = false;
          validationErrors.push('Cidade de instalação obrigatória');
        }

        if (!adicionalData.estado_inst) {
          isValid = false;
          validationErrors.push('Estado de instalação obrigatório');
        }

        if (!adicionalData.cep_inst) {
          isValid = false;
          validationErrors.push('CEP de instalação obrigatório');
        }

        adicionalData.valid = isValid;
        adicionalData.validationErrors = validationErrors;

        data.push(adicionalData);
      });

      return data;
    } catch (error) {
      console.error('Erro ao processar dados da API:', error);
      throw new Error('Erro ao processar dados da API');
    }
  };

  const handleApiUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiUrl(event.target.value);
    setError(null);
  };

  const handleApiTokenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiToken(event.target.value);
    setError(null);
  };

  const handleFetchData = async () => {
    if (!apiUrl.trim()) {
      setError('URL da API é obrigatória');
      return;
    }

    if (!apiToken.trim()) {
      setError('Token da API é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fazer requisição para a API externa
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${apiToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status} - ${response.statusText}`);
      }

      const apiData = await response.json();

      // Verificar se a resposta contém dados de adicionais
      let adicionaisData: any[];

      if (Array.isArray(apiData)) {
        adicionaisData = apiData;
      } else if (apiData && typeof apiData === 'object') {
        // Procurar por propriedades que contêm arrays de adicionais
        const possibleArrayKeys = Object.keys(apiData).filter(key =>
          Array.isArray(apiData[key])
        );

        if (possibleArrayKeys.length === 0) {
          throw new Error('Nenhum array de adicionais encontrado na resposta da API');
        }

        // Usar o primeiro array encontrado
        const arrayKey = possibleArrayKeys[0];
        adicionaisData = apiData[arrayKey];
        console.log(`Array de adicionais encontrado na propriedade: ${arrayKey}`);
      } else {
        throw new Error('Formato de resposta inválido da API');
      }

      if (!Array.isArray(adicionaisData) || adicionaisData.length === 0) {
        throw new Error('Nenhum adicional encontrado na resposta da API');
      }

      // Processar dados da API
      const parsedData = parseApiData(adicionaisData);

      // Ordenar dados: primeiro inválidos, depois válidos
      const sortedData = parsedData.sort((a, b) => {
        // Primeiro critério: inválidos primeiro
        if (!a.valid && b.valid) return -1;
        if (a.valid && !b.valid) return 1;

        // Segundo critério: ordenar por ID (numérico se possível)
        const idA = parseInt(a.id) || 0;
        const idB = parseInt(b.id) || 0;
        return idA - idB;
      });

      setParsedData(sortedData);
      setImportStats({
        total: parsedData.length,
        valid: parsedData.filter(item => item.valid).length,
        invalid: parsedData.filter(item => !item.valid).length
      });
      setSelectedRows(new Set()); // Limpar seleção ao buscar novos dados

      setActiveStep(1);
      toast.success(`Dados obtidos com sucesso! ${parsedData.length} registros encontrados.`);
    } catch (err: any) {
      console.error('Erro ao buscar dados da API:', err);
      setError(err.message || 'Erro ao conectar com a API. Verifique a URL e o token.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartImport = async () => {
    setImporting(true);
    setImportProgress({});
    setImportCompleted(false);

    try {
      // Filtrar apenas os registros SELECIONADOS e válidos
      const selectedPppoeUsers = Array.from(selectedRows);
      const selectedData = parsedData.filter((item) =>
        selectedPppoeUsers.includes(item.usuario_pppoe) && item.valid
      );

      if (selectedData.length === 0) {
        throw new Error('Nenhum adicional selecionado ou válido para importar');
      }

      // Marcar todos os registros selecionados como "processando"
      const initialProgress: { [key: string]: ImportProgress } = {};
      selectedData.forEach(adicional => {
        initialProgress[adicional.usuario_pppoe] = {
          nome: adicional.contrato_titulo || adicional.login,
          status: 'processando'
        };
      });
      setImportProgress(initialProgress);

      console.log(`🚀 Iniciando importação em lote de ${selectedData.length} adicionais selecionados...`);

      // Usar o serviço de importação
      const result = await importService.importAdicionais({
        tableData: selectedData // Apenas os registros SELECIONADOS
      });

      if (result.success) {
        const { summary, results } = result.data;

        // Atualizar status baseado nos resultados individuais
        const finalProgress: { [key: string]: ImportProgress } = {};

        // Processar resultados individuais se disponíveis
        if (results && Array.isArray(results)) {
          results.forEach((adicionalResult: any) => {
            // Buscar pelo usuario_pppoe (ID único) retornado do backend
            if (adicionalResult.usuario_pppoe) {
              const adicional = selectedData.find(item => item.usuario_pppoe === adicionalResult.usuario_pppoe);

              if (adicional) {
                finalProgress[adicional.usuario_pppoe] = {
                  nome: adicional.contrato_titulo || adicional.login,
                  status: adicionalResult.status === 'sucesso' ? 'sucesso' : 'erro',
                  erro: adicionalResult.status === 'erro' ? adicionalResult.erro : undefined
                };
              }
            }
          });
        } else {
          // Se não há resultados individuais, usar sumário geral
          selectedData.forEach(adicional => {
            finalProgress[adicional.usuario_pppoe] = {
              nome: adicional.contrato_titulo || adicional.login,
              status: 'sucesso' // Assumir sucesso se não há erro específico
            };
          });
        }

        setImportProgress(finalProgress);
        setImportCompleted(true);

        // Mostrar resultado final baseado no sumário
        if (summary.success > 0) {
          toast.success(`${summary.success} adicionais importados com sucesso!`);
        }

        if (summary.errors > 0) {
          toast.warning(`${summary.errors} adicionais não puderam ser importados. Verifique os detalhes na tabela.`);
        }

        // Se todos foram importados com sucesso, chamar onSuccess
        if (summary.errors === 0) {
          setTimeout(() => onSuccess(), 2000);
        }

        // Limpar seleção APENAS dos registros importados com SUCESSO
        const successfulImports = results.filter((result: any) => {
          const status = result.status?.toLowerCase();
          return status === 'sucesso' || status === 'success';
        });

        if (successfulImports.length > 0) {
          // Identificar usuario_pppoe dos registros importados com sucesso
          const successfulPppoeUsers: string[] = [];

          successfulImports.forEach((result: any) => {
            if (result.usuario_pppoe) {
              const adicional = selectedData.find(item => item.usuario_pppoe === result.usuario_pppoe);
              if (adicional) {
                successfulPppoeUsers.push(adicional.usuario_pppoe);
              }
            }
          });

          // Desmarcar APENAS os registros importados com sucesso
          const newSelectedRows = new Set(selectedRows);
          successfulPppoeUsers.forEach((pppoe: string) => {
            newSelectedRows.delete(pppoe);
          });
          setSelectedRows(newSelectedRows);
        }

      } else {
        throw new Error(result.message || 'Erro na importação em lote');
      }

    } catch (err: any) {
      console.error('❌ Erro ao importar adicionais em lote:', err);

      // Marcar todos os selecionados como erro se a requisição falhou completamente
      const errorProgress: { [key: string]: ImportProgress } = {};
      const selectedPppoeUsers = Array.from(selectedRows);
      const selectedData = parsedData.filter((item) =>
        selectedPppoeUsers.includes(item.usuario_pppoe) && item.valid
      );

      selectedData.forEach(adicional => {
        errorProgress[adicional.usuario_pppoe] = {
          nome: adicional.contrato_titulo || adicional.login,
          status: 'erro',
          erro: err.message || 'Erro na importação'
        };
      });

      setImportProgress(errorProgress);
      setError(err.message || 'Erro ao importar adicionais. Tente novamente.');
      toast.error(err.message || 'Erro ao importar adicionais');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setApiUrl('');
    setApiToken('');
    setParsedData([]);
    setError(null);
    setLoading(false);
    setImporting(false);
    setImportProgress({});
    setImportCompleted(false);
    setSelectedRows(new Set());
    onClose();
  };

  const renderUploadStep = () => (
    <Box>
      <Typography variant="body1" gutterBottom>
        Conecte-se à API externa para importar dados de adicionais
      </Typography>

      <Card sx={{ mt: 3, p: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <CloudUpload sx={{ fontSize: '4rem', color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Configuração da API
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Informe a URL e token de acesso da API externa
              </Typography>
            </Box>

            <TextField
              label="URL da API"
              placeholder="https://api.exemplo.com/adicionais"
              value={apiUrl}
              onChange={handleApiUrlChange}
              fullWidth
              variant="outlined"
              helperText="URL completa da API que retorna dados de adicionais"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <CloudUpload color="action" />
                  </Box>
                )
              }}
            />

            <TextField
              label="Token de Acesso"
              placeholder="seu_token_de_acesso_aqui"
              value={apiToken}
              onChange={handleApiTokenChange}
              fullWidth
              variant="outlined"
              type="password"
              helperText="Token Bearer para autenticação na API"
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                    <Description color="action" />
                  </Box>
                )
              }}
            />

            {apiUrl && apiToken && (
              <Box sx={{
                mt: 2,
                p: 2,
                backgroundColor: 'success.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.main'
              }}>
                <Typography variant="body2" color="success.dark">
                  ✓ Configuração da API preenchida. Clique em "Buscar Dados" para continuar.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderAnalysisStep = () => (
    <Box>

      {/* Barra de progresso durante importação */}
      {importing && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Importando adicionais...
          </Typography>
        </Box>
      )}

      {/* Estatísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {importStats.total}
              </Typography>
              <Typography variant="caption">
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {importStats.valid}
              </Typography>
              <Typography variant="caption">
                Válidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {importStats.invalid}
              </Typography>
              <Typography variant="caption">
                Inválidos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contador de seleção */}
      {selectedRows.size > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Alert severity="info" sx={{ flex: 1 }}>
            <strong>{selectedRows.size}</strong> {selectedRows.size === 1 ? 'registro selecionado' : 'registros selecionados'}
          </Alert>
          <Button
            onClick={() => setSelectedRows(new Set())}
            size="small"
            sx={{ ml: 2 }}
            variant="outlined"
          >
            Limpar Seleção
          </Button>
        </Box>
      )}

      {/* DataGrid com paginação e checkbox customizado */}
      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={parsedData}
          getRowId={(row) => row.usuario_pppoe}
          columns={[
            {
              field: 'checkbox',
              headerName: '',
              width: 50,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              renderHeader: () => (
                <IconButton
                  size="small"
                  onClick={handleSelectAll}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.size === 0 ? (
                    <CheckBoxOutlineBlank fontSize="small" />
                  ) : selectedRows.size === parsedData.length ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <IndeterminateCheckBox fontSize="small" color="primary" />
                  )}
                </IconButton>
              ),
              renderCell: (params) => (
                <IconButton
                  size="small"
                  onClick={() => handleSelectRow(params.row.usuario_pppoe)}
                  sx={{ padding: 0 }}
                >
                  {selectedRows.has(params.row.usuario_pppoe) ? (
                    <CheckBox fontSize="small" color="primary" />
                  ) : (
                    <CheckBoxOutlineBlank fontSize="small" />
                  )}
                </IconButton>
              ),
            },
            {
              field: 'status',
              headerName: 'Status',
              width: 150,
              renderCell: (params) => {
                const row = params.row;
                const usuario_pppoe = params.row.usuario_pppoe;
                const progress = importProgress[usuario_pppoe];

                // Se está importando ou há progresso, mostrar status de importação
                if (importing || progress) {
                  if (progress?.status === 'processando') {
                    return (
                      <Chip
                        icon={<Sync className="animate-spin" />}
                        label="Importando..."
                        color="info"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'sucesso') {
                    return (
                      <Chip
                        icon={<CheckCircle />}
                        label="Importado"
                        color="success"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  } else if (progress?.status === 'erro') {
                    return (
                      <Chip
                        icon={<ErrorIcon />}
                        label="Erro"
                        color="error"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                        title={progress.erro}
                      />
                    );
                  } else if (importing && row.valid) {
                    return (
                      <Chip
                        icon={<Schedule />}
                        label="Aguardando"
                        color="default"
                        size="small"
                        sx={{ height: '24px', fontSize: '0.75rem' }}
                      />
                    );
                  }
                }

                // Status padrão de validação
                if (row.valid) {
                  return (
                    <Chip
                      icon={<CheckCircle />}
                      label="Válido"
                      color="success"
                      size="small"
                      sx={{ height: '24px', fontSize: '0.75rem' }}
                    />
                  );
                } else {
                  return (
                    <Chip
                      icon={<ErrorIcon />}
                      label="Inválido"
                      color="error"
                      size="small"
                      sx={{ height: '24px', fontSize: '0.75rem' }}
                    />
                  );
                }
              }
            },
            {
              field: 'erro',
              headerName: 'Erro',
              width: 250,
              renderCell: (params) => {
                const usuario_pppoe = params.row.usuario_pppoe;
                const progress = importProgress[usuario_pppoe];
                const validationErrors = params.row.validationErrors;

                if (progress?.erro) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {progress.erro}
                    </Typography>
                  );
                } else if (validationErrors && validationErrors.length > 0) {
                  return (
                    <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                      {validationErrors.join(', ')}
                    </Typography>
                  );
                }
                return '-';
              }
            },
            ...(parsedData.length > 0
              ? Object.keys(parsedData[0])
                .filter(key => !['valid', 'duplicate', 'validationErrors', 'isEditing', 'rowIndex'].includes(key))
                .map(key => ({
                  field: key,
                  headerName: key.replace(/_/g, ' ').toUpperCase(),
                  width: 150,
                  renderCell: (params: any) => {
                    const row = params.row;
                    const fieldErrors = getFieldValidationErrors(row);
                    const hasError = fieldErrors[key];

                    return (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          border: hasError ? '1px dashed #f44336' : 'none',
                          backgroundColor: hasError ? '#ffebee' : 'transparent',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {String(params.value || '')}
                        </Typography>
                      </Box>
                    );
                  }
                }))
              : []
            )
          ]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 100, page: 0 },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          density="compact"
          sx={{
            '& .MuiDataGrid-cell': {
              padding: '4px 8px',
              fontSize: '0.75rem',
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '0.75rem',
              fontWeight: 'bold',
            },
          }}
        />
      </Box>
    </Box>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderUploadStep();
      case 1:
        return renderAnalysisStep();
      default:
        return null;
    }
  };

  const getActionButtons = () => {
    switch (activeStep) {
      case 0:
        return (
          <Button
            variant="contained"
            onClick={handleFetchData}
            disabled={!apiUrl || !apiToken || loading}
            startIcon={<CloudUpload />}
          >
            {loading ? 'Buscando Dados...' : 'Buscar Dados'}
          </Button>
        );
      case 1:
        const selectedValidCount = Array.from(selectedRows).filter(usuario_pppoe => {
          const item = parsedData.find(d => d.usuario_pppoe === usuario_pppoe);
          return item?.valid;
        }).length;

        return (
          <>
            <Button onClick={() => setActiveStep(0)} disabled={importing}>
              Voltar
            </Button>
            <Button
              variant="contained"
              onClick={handleStartImport}
              disabled={selectedValidCount === 0 || importing}
              color="success"
              startIcon={importing ? <Sync className="animate-spin" /> : <CloudUpload />}
            >
              {importing
                ? `Importando ${selectedValidCount} adicionais selecionados...`
                : importCompleted
                  ? 'Importação Concluída'
                  : selectedRows.size === 0
                    ? 'Selecione adicionais para importar'
                    : `Importar ${selectedValidCount} adicional${selectedValidCount !== 1 ? 'is' : ''} selecionado${selectedValidCount !== 1 ? 's' : ''}`
              }
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Importar Adicionais MKAuth
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        {getStepContent()}

        {/* CSS para animação */}
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .animate-spin {
              animation: spin 1s linear infinite;
            }
          `}
        </style>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {getActionButtons()}
      </DialogActions>
    </Dialog>
  );
};

export default ImportAdicionalModal; 